// src/pages/Users/Techniques/utils/uv2TrainingUtils.js

import {
  UV2_DEFENSE_ATTACKS,
  UV2_GRADE_CONFIG,
  UV2_PROGRAM_BY_GRADE,
} from "../data/uv2ProgramData";

import { strip } from "./techniquesUtils";

export function normalizeUv2TechniqueName(value = "") {
  if (!value) return "";

  const withoutParentheses = String(value).replace(/\([^)]*\)/g, " ");

  return strip(withoutParentheses)
    .replace(/['’]/g, "-")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function compactName(value = "") {
  return normalizeUv2TechniqueName(value).replace(/-/g, "");
}

export function buildTechniqueLookup(allTechniques = []) {
  const lookup = new Map();

  const safeTechniques = Array.isArray(allTechniques)
    ? allTechniques.filter(Boolean)
    : [];

  safeTechniques.forEach((technique) => {
    const names = [
      technique?.nom,
      technique?.nom_japonais,
      technique?.name,
      technique?.japanese_name,
    ].filter(Boolean);

    names.forEach((name) => {
      const normalized = normalizeUv2TechniqueName(name);
      const compact = compactName(name);

      if (normalized && !lookup.has(normalized)) {
        lookup.set(normalized, technique);
      }

      if (compact && !lookup.has(compact)) {
        lookup.set(compact, technique);
      }
    });
  });

  return lookup;
}

export function resolveTechniqueLink(name, lookup) {
  if (!name || !lookup) return null;

  const normalized = normalizeUv2TechniqueName(name);
  const compact = compactName(name);

  return lookup.get(normalized) || lookup.get(compact) || null;
}

function shuffle(items = []) {
  return [...items].sort(() => Math.random() - 0.5);
}

function randomItem(items = []) {
  if (!items.length) return null;
  return items[Math.floor(Math.random() * items.length)];
}

function pickAcrossFamilies(section = {}, count = 1, domain = "") {
  const entries = Object.entries(section).filter(
    ([, names]) => Array.isArray(names) && names.length > 0,
  );

  const picked = [];
  const usedNames = new Set();

  const shuffledFamilies = shuffle(entries);

  shuffledFamilies.forEach(([family, names]) => {
    if (picked.length >= count) return;

    const available = names.filter(
      (name) => !usedNames.has(normalizeUv2TechniqueName(name)),
    );

    const selected = randomItem(available);

    if (!selected) return;

    usedNames.add(normalizeUv2TechniqueName(selected));

    picked.push({
      id: `${domain}-${family}-${selected}-${picked.length}`,
      domain,
      family,
      name: selected,
    });
  });

  const remaining = shuffle(
    entries.flatMap(([family, names]) =>
      names
        .filter((name) => !usedNames.has(normalizeUv2TechniqueName(name)))
        .map((name) => ({
          id: `${domain}-${family}-${name}`,
          domain,
          family,
          name,
        })),
    ),
  );

  while (picked.length < count && remaining.length > 0) {
    const selected = remaining.shift();
    usedNames.add(normalizeUv2TechniqueName(selected.name));
    picked.push({
      ...selected,
      id: `${selected.id}-${picked.length}`,
    });
  }

  return picked;
}

function pickDefense(series = [], count = 2) {
  const pool = series.flatMap((serie) =>
    (UV2_DEFENSE_ATTACKS[serie] || []).map((name) => ({
      id: `defense-${serie}-${name}`,
      domain: "Défense",
      family: `Série ${serie}`,
      serie,
      name,
    })),
  );

  return shuffle(pool).slice(0, count);
}

export function generateUv2Draw(gradeKey = "1dan") {
  const config =
    UV2_GRADE_CONFIG.find((item) => item.key === gradeKey) ||
    UV2_GRADE_CONFIG[0];

  const program = UV2_PROGRAM_BY_GRADE[config.key];

  if (!program) {
    return null;
  }

  return {
    id: `uv2-${config.key}-${Date.now()}`,
    generatedAt: new Date().toISOString(),
    gradeKey: config.key,
    nage: pickAcrossFamilies(program.nage, config.draw.nage, "Nage-waza"),
    newaza: pickAcrossFamilies(program.newaza, config.draw.newaza, "Ne-waza"),
    defense: pickDefense(config.defenseSeries, config.draw.defense),
  };
}

export function countProgramItems(section = {}) {
  return Object.values(section).reduce((total, names) => {
    if (!Array.isArray(names)) return total;
    return total + names.length;
  }, 0);
}